(function() {
  if (window.ACEPerformanceGuard && window.ACEPerformanceGuard.version) return;

  var root = document.documentElement;
  var nav = window.navigator || {};
  var connection = nav.connection || nav.mozConnection || nav.webkitConnection;
  var cores = nav.hardwareConcurrency || 4;
  var memory = nav.deviceMemory || 4;
  var coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var saveData = Boolean(connection && connection.saveData);
  var slowNetwork = Boolean(connection && /(^2g$|slow-2g|3g)/i.test(connection.effectiveType || ''));
  var narrow = Math.min(window.innerWidth || 9999, (window.screen && window.screen.width) || 9999) < 760;
  var perf = 'high';
  var videoObserver = null;
  var animationObserver = null;
  var registry = new WeakMap();
  var activeVideos = [];
  var started = false;
  var debug = /\bace_perf_debug=true\b/.test(window.location.search || '');

  if (reducedMotion || saveData || slowNetwork || memory <= 2 || cores <= 2) {
    perf = 'low';
  } else if (coarse || narrow || memory <= 4 || cores <= 4) {
    perf = 'medium';
  }

  var maxActiveAutoplay = perf === 'high' && !coarse && !narrow ? 2 : perf === 'medium' ? 1 : 0;

  root.classList.add('ace-perf-' + perf);
  if (coarse) root.classList.add('ace-touch');
  if (reducedMotion) root.classList.add('ace-reduced-motion');
  if (saveData || slowNetwork) root.classList.add('ace-data-saver');

  function log(message, detail) {
    if (!debug || !window.console) return;
    window.console.info('[ACE performance]', message, detail || '');
  }

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function isAutoplayCandidate(video) {
    return video.hasAttribute('autoplay') || video.dataset.aceAutoplay === 'true' || video.closest('[autoplay]');
  }

  function prepareVideo(video) {
    if (!video || registry.has(video)) return;

    var state = {
      visible: false,
      ratio: 0,
      userPaused: false,
      wantedAutoplay: isAutoplayCandidate(video),
      lastRequest: 0
    };

    registry.set(video, state);

    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    if (state.wantedAutoplay) {
      video.muted = true;
      video.defaultMuted = true;
      video.setAttribute('muted', '');
      video.setAttribute('preload', 'metadata');
      video.dataset.aceManagedAutoplay = 'true';
    } else if (!video.hasAttribute('preload')) {
      video.setAttribute('preload', 'metadata');
    }

    video.addEventListener('pause', function() {
      if (video.dataset.aceProgrammaticPause === 'true') {
        video.dataset.aceProgrammaticPause = 'false';
        return;
      }
      state.userPaused = true;
      removeActive(video);
    });

    video.addEventListener('play', function() {
      state.userPaused = false;
      registerActive(video);
    });

    video.addEventListener('error', function() {
      removeActive(video);
      log('Video error', video.currentSrc || video.src);
    });

    if (videoObserver) videoObserver.observe(video);
  }

  function removeActive(video) {
    activeVideos = activeVideos.filter(function(item) {
      return item !== video;
    });
  }

  function registerActive(video) {
    removeActive(video);
    activeVideos.push(video);
    enforceVideoBudget(video);
  }

  function safePause(video) {
    if (!video || video.paused) return;
    video.dataset.aceProgrammaticPause = 'true';
    try {
      video.pause();
    } catch (error) {
      log('Pause failed', error);
    }
    removeActive(video);
  }

  function safePlay(video, options) {
    if (!video) return Promise.resolve(false);
    if (!registry.has(video)) prepareVideo(video);
    var state = registry.get(video);
    if (!state) return Promise.resolve(false);
    if (document.hidden || !video.isConnected) return Promise.resolve(false);
    if (state.userPaused && !(options && options.force)) return Promise.resolve(false);
    if ((reducedMotion || saveData || slowNetwork) && !(options && options.force)) return Promise.resolve(false);
    if (!state.visible && !(options && options.force)) return Promise.resolve(false);
    if (state.wantedAutoplay) {
      video.muted = !(options && options.withSound);
      if (video.muted) video.setAttribute('muted', '');
    }

    state.lastRequest = Date.now();

    var playPromise;
    try {
      playPromise = video.play();
    } catch (error) {
      log('Play threw synchronously', error);
      return Promise.resolve(false);
    }

    if (playPromise && typeof playPromise.catch === 'function') {
      return playPromise
        .then(function() {
          registerActive(video);
          return true;
        })
        .catch(function(error) {
          if (options && options.withSound) {
            video.muted = true;
            video.setAttribute('muted', '');
          }
          log('Autoplay/play rejected', error);
          return false;
        });
    }

    registerActive(video);
    return Promise.resolve(true);
  }

  function enforceVideoBudget(preferredVideo) {
    var activeAutoplay = activeVideos.filter(function(video) {
      var state = registry.get(video);
      return state && state.wantedAutoplay && !video.paused && video.isConnected;
    });

    if (maxActiveAutoplay < 1) {
      activeAutoplay.forEach(safePause);
      return;
    }

    activeAutoplay.sort(function(a, b) {
      if (a === preferredVideo) return 1;
      if (b === preferredVideo) return -1;
      var aState = registry.get(a) || {};
      var bState = registry.get(b) || {};
      return (aState.ratio || 0) - (bState.ratio || 0);
    });

    while (activeAutoplay.length > maxActiveAutoplay) {
      safePause(activeAutoplay.shift());
    }
  }

  function handleVideoEntries(entries) {
    entries.forEach(function(entry) {
      var video = entry.target;
      var state = registry.get(video);
      if (!state) return;

      state.visible = entry.isIntersecting && entry.intersectionRatio >= 0.18;
      state.ratio = entry.isIntersecting ? entry.intersectionRatio : 0;

      if (!state.visible) {
        safePause(video);
        return;
      }

      if (state.wantedAutoplay) {
        safePlay(video);
      }
    });

    enforceVideoBudget();
  }

  function initVideoObserver() {
    if (!('IntersectionObserver' in window) || videoObserver) return;

    videoObserver = new IntersectionObserver(handleVideoEntries, {
      rootMargin: '140px 0px',
      threshold: [0, 0.18, 0.35, 0.66, 0.9]
    });
  }

  function initVideos(scope) {
    initVideoObserver();
    toArray((scope || document).querySelectorAll('video')).forEach(prepareVideo);
  }

  function initDecorativeAnimations(scope) {
    if (!('IntersectionObserver' in window)) return;
    if (!animationObserver) {
      animationObserver = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          entry.target.style.animationPlayState = entry.isIntersecting ? 'running' : 'paused';
        });
      }, {
        rootMargin: '160px 0px'
      });
    }

    toArray((scope || document).querySelectorAll(
      '.ace-top-menu__stars, .ace-top-menu__stars--near, .ace-top-menu__stars--far, .ace-top-menu__aurora, .ace-top-menu__veil, ' +
      '.ace-immersive__moment-stars, .ace-proof__stars, .ace-services__haze, .ace-pull-image__stars, ' +
      '.ace-hero-video__glow, .ace-page-aura, .ace-energy-field, .ace-orb'
    )).forEach(function(node) {
      animationObserver.observe(node);
    });
  }

  function syncHiddenTabWork() {
    toArray(document.querySelectorAll(
      '.ace-top-menu__stars, .ace-top-menu__stars--near, .ace-top-menu__stars--far, .ace-top-menu__aurora, .ace-top-menu__veil, ' +
      '.ace-immersive__moment-stars, .ace-proof__stars, .ace-services__haze, .ace-pull-image__stars, ' +
      '.ace-hero-video__glow, .ace-page-aura, .ace-energy-field, .ace-orb'
    )).forEach(function(node) {
      node.style.animationPlayState = document.hidden ? 'paused' : '';
    });

    if (document.hidden) {
      toArray(document.querySelectorAll('video')).forEach(function(video) {
        if (!video.paused && !video.hasAttribute('data-ace-keep-playing-hidden')) {
          video.dataset.acePausedHidden = 'true';
          safePause(video);
        }
      });
      return;
    }

    toArray(document.querySelectorAll('video[data-ace-paused-hidden="true"]')).forEach(function(video) {
      video.dataset.acePausedHidden = 'false';
      var state = registry.get(video);
      if (state && state.wantedAutoplay) safePlay(video);
    });
  }

  function init(scope) {
    initDecorativeAnimations(scope || document);
    initVideos(scope || document);
  }

  function cleanup(scope) {
    if (!scope) return;
    toArray(scope.querySelectorAll('video')).forEach(function(video) {
      safePause(video);
      if (videoObserver) videoObserver.unobserve(video);
      removeActive(video);
    });
  }

  function ready() {
    if (started) return;
    started = true;
    init(document);
    syncHiddenTabWork();
  }

  document.addEventListener('visibilitychange', syncHiddenTabWork);
  document.addEventListener('shopify:section:load', function(event) {
    init(event.target);
  });
  document.addEventListener('shopify:section:unload', function(event) {
    cleanup(event.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ready, { once: true });
  } else {
    ready();
  }

  window.ACEPerformanceGuard = {
    version: '2026-06-19',
    perf: perf,
    init: init,
    cleanup: cleanup,
    safePlay: safePlay,
    safePause: safePause,
    pauseAll: function() {
      toArray(document.querySelectorAll('video')).forEach(safePause);
    }
  };
})();
